import { assertIsDefined } from '@gitbook/util-ts';

import type * as protocol from './protocol';

type WithRequiredFields<T, K extends keyof T> = T & {
    [Field in K]-?: NonNullable<T[Field]>;
};

type RequiredContentOperation = WithRequiredFields<
    protocol.IContentOperation,
    'id' | 'status' | 'createdAt' | 'updatedAt' | 'updateId' | 'expireAfter' | 'cursor' | 'target'
>;

type RequiredPresence = WithRequiredFields<
    protocol.IPresence,
    'clientId' | 'joinTimestamp' | 'updateTimestamp'
>;

type RequiredContentMarker = WithRequiredFields<
    protocol.IContentMarker,
    'markerId' | 'type' | 'author' | 'anchorRelativePosition' | 'focusRelativePosition'
>;

type RequiredRPCRequestMessage = WithRequiredFields<
    protocol.IRPCRequestMessage,
    'id' | 'method' | 'payload'
>;

type RequiredRPCReplyMessage = WithRequiredFields<protocol.IRPCReplyMessage, 'replyTo'>;

type RequiredRPCError = WithRequiredFields<protocol.IRPCError, 'code' | 'message'>;

function requireField<T, Field extends keyof T>(message: T, field: Field): NonNullable<T[Field]> {
    const value = message[field];
    assertIsDefined(value);
    return value;
}

/**
 * Validates and returns an operation with fields that were required before the proto3 migration.
 */
export function requireContentOperation(
    operation: protocol.IContentOperation | null | undefined
): RequiredContentOperation {
    assertIsDefined(operation);
    requireField(operation, 'id');
    requireField(operation, 'status');
    requireField(operation, 'createdAt');
    requireField(operation, 'updatedAt');
    requireField(operation, 'updateId');
    requireField(operation, 'expireAfter');
    requireField(operation, 'cursor');
    requireField(operation, 'target');

    return operation as RequiredContentOperation;
}

/**
 * Validates and returns a presence with fields that were required before the proto3 migration.
 */
export function requirePresence(presence: protocol.IPresence): RequiredPresence {
    requireField(presence, 'clientId');
    requireField(presence, 'joinTimestamp');
    requireField(presence, 'updateTimestamp');

    return presence as RequiredPresence;
}

/**
 * Validates and returns a content marker with fields that were required before the proto3 migration.
 */
export function requireContentMarker(marker: protocol.IContentMarker): RequiredContentMarker {
    requireField(marker, 'markerId');
    requireField(marker, 'type');
    requireField(marker, 'author');
    requireField(marker, 'anchorRelativePosition');
    requireField(marker, 'focusRelativePosition');

    return marker as RequiredContentMarker;
}

/**
 * Returns the binary update from an init message, preserving the previous required-field contract.
 */
export function requireInitMessageUpdate(message: protocol.IInitMessage): Uint8Array {
    return requireField(message, 'update');
}

/**
 * Returns the binary update from an update message, preserving the previous required-field contract.
 */
export function requireUpdateMessageUpdate(message: protocol.IUpdateMessage): Uint8Array {
    return requireField(message, 'update');
}

/**
 * Returns the state vector from a prompt-init message, preserving the previous required-field contract.
 */
export function requirePromptInitMessageStateVector(
    message: protocol.IPromptInitMessage
): Uint8Array {
    return requireField(message, 'stateVector');
}

/**
 * Validates and returns an RPC request with fields required by the dispatcher.
 */
export function requireRPCRequestMessage(
    message: protocol.IRPCRequestMessage
): RequiredRPCRequestMessage {
    requireField(message, 'id');
    requireField(message, 'method');
    requireField(message, 'payload');

    return message as RequiredRPCRequestMessage;
}

/**
 * Validates and returns an RPC reply with fields required by the reply matcher.
 */
export function requireRPCReplyMessage(
    message: protocol.IRPCReplyMessage
): RequiredRPCReplyMessage {
    requireField(message, 'replyTo');

    return message as RequiredRPCReplyMessage;
}

/**
 * Validates and returns an RPC error with fields required by error handling.
 */
export function requireRPCError(error: protocol.IRPCError): RequiredRPCError {
    requireField(error, 'code');
    requireField(error, 'message');

    return error as RequiredRPCError;
}

/**
 * Returns the subscriptionId from any DataSync message type.
 * Returns undefined when the message carries no subscriptionId.
 */
export function getSubscriptionId(message: protocol.IMessage): number | undefined {
    if (message.promptInit) return message.promptInit.subscriptionId || undefined;
    if (message.init) return message.init.subscriptionId || undefined;
    if (message.update) return message.update.subscriptionId || undefined;
    if (message.updatePresence) return message.updatePresence.subscriptionId || undefined;
    if (message.broadcastOperation) return message.broadcastOperation.subscriptionId || undefined;
    if (message.broadcastPresences) return message.broadcastPresences.subscriptionId || undefined;
    if (message.ack) return message.ack.subscriptionId || undefined;
    if (message.rpcRequest) return message.rpcRequest.subscriptionId || undefined;
    if (message.rpcReply) return message.rpcReply.subscriptionId || undefined;
    if (message.broadcastMarkers) return message.broadcastMarkers.subscriptionId || undefined;
    return undefined;
}

/**
 * Returns the message ID from a DataSync message.
 */
export function getMessageId(message: protocol.IMessage): number {
    if (message.promptInit) {
        return requireField(message.promptInit, 'id');
    }

    if (message.init) {
        return requireField(message.init, 'id');
    }

    if (message.update) {
        return requireField(message.update, 'id');
    }

    if (message.updatePresence) {
        return requireField(message.updatePresence, 'id');
    }

    if (message.broadcastOperation) {
        return requireField(message.broadcastOperation, 'id');
    }

    if (message.broadcastPresences) {
        return requireField(message.broadcastPresences, 'id');
    }

    if (message.ack) {
        return requireField(message.ack, 'id');
    }

    if (message.rpcRequest) {
        return requireField(message.rpcRequest, 'id');
    }

    if (message.rpcReply) {
        return requireField(message.rpcReply, 'id');
    }

    if (message.broadcastMarkers) {
        return requireField(message.broadcastMarkers, 'id');
    }

    throw new Error(`Unknown message type: ${JSON.stringify(message)}`);
}
